import React from 'react';
import autoBind from 'auto-bind';
import { Table } from 'semantic-ui-react';
import PropTypes from 'prop-types';
import { renderCouponStatusText } from 'components/renderUtils';
import { formatCurrencyRupiah } from 'utils/currencyUtils';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import DefaultPagination from 'components/DefaultPagination';
import OnOffButton from 'pages/Coupons/OnOffButton';

function renderCouponMaxDiscount(maxDiscount) {
  return maxDiscount ? formatCurrencyRupiah(maxDiscount) : '-';
}

function renderAdditionalMonth(additionalMonth) {
  return additionalMonth && additionalMonth > 0
    ? `${additionalMonth} Months`
    : '-';
}

function renderQuotaType(includesAllBusinesses) {
  return includesAllBusinesses ? 'Coupon Quantity' : 'Merchant Quantity';
}

function canStatusChanged(coupon) {
  const { global_quota: globalQuota, used_quota: usedQuota, status } = coupon;
  return !(!!globalQuota && usedQuota >= globalQuota) && status !== 'EXPIRED';
}

class CouponsList extends React.Component {
  constructor(props) {
    super(props);
    autoBind(this);
  }

  onPageChange(event, { activePage }) {
    const { onPageChange } = this.props;
    onPageChange(activePage);
  }

  handleToggleStatusRequest(coupon) {
    const { onCouponStatusChanged } = this.props;
    onCouponStatusChanged(coupon.code, coupon.status);
  }

  renderTableContent() {
    const {
      isLoading,
      coupons: { list, totalCount },
    } = this.props;
    if (isLoading) {
      return (
        <Table.Row>
          <Table.Cell colSpan="8">
            <LoadingSpinner />
          </Table.Cell>
        </Table.Row>
      );
    }

    if (totalCount === 0) {
      return (
        <Table.Row>
          <Table.Cell colSpan="8" style={{ textAlign: 'center' }}>
            No data
          </Table.Cell>
        </Table.Row>
      );
    }

    return list.map(this.renderTableRow);
  }

  renderTableRow(coupon) {
    const { onShowingCouponDetails } = this.props;
    return (
      <Table.Row
        key={coupon.id}
        style={{ cursor: 'pointer' }}
        onClick={() => onShowingCouponDetails(coupon.code)}
      >
        <Table.Cell>{coupon.code}</Table.Cell>
        <Table.Cell>{coupon.amount}%</Table.Cell>
        <Table.Cell>
          {renderCouponMaxDiscount(coupon.max_discount_per_application)}
        </Table.Cell>
        <Table.Cell>
          {renderAdditionalMonth(coupon.additional_month)}
        </Table.Cell>
        <Table.Cell>
          {renderQuotaType(coupon.includes_all_businesses)}
        </Table.Cell>
        <Table.Cell>
          {coupon.used_quota}/{coupon.global_quota}
        </Table.Cell>
        <Table.Cell>{renderCouponStatusText(coupon)}</Table.Cell>
        <Table.Cell>
          {canStatusChanged(coupon) && (
            <OnOffButton
              couponActive={coupon.status === 'OPEN'}
              onClick={e => {
                e.stopPropagation();
                this.handleToggleStatusRequest(coupon);
              }}
            />
          )}
        </Table.Cell>
      </Table.Row>
    );
  }

  render() {
    const {
      coupons: { pageNumber, pageSize, totalCount },
      isLoading,
    } = this.props;
    return (
      <div>
        <Table celled selectable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Coupon Code</Table.HeaderCell>
              <Table.HeaderCell>Percentage</Table.HeaderCell>
              <Table.HeaderCell>Max Discounts</Table.HeaderCell>
              <Table.HeaderCell>Additional Months</Table.HeaderCell>
              <Table.HeaderCell>Quota Type</Table.HeaderCell>
              <Table.HeaderCell>Coupons Used</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell />
            </Table.Row>
          </Table.Header>
          <Table.Body>{this.renderTableContent()}</Table.Body>
        </Table>
        {!isLoading && totalCount > 0 && (
          <DefaultPagination
            activePage={pageNumber}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={this.onPageChange}
          />
        )}
      </div>
    );
  }
}

CouponsList.propTypes = {
  coupons: PropTypes.shape({
    list: PropTypes.arrayOf(
      PropTypes.shape({
        code: PropTypes.string,
        status: PropTypes.string,
        amount: PropTypes.number,
        global_quota: PropTypes.number,
        used_quota: PropTypes.number,
      })
    ),
    pageNumber: PropTypes.number.isRequired,
    pageSize: PropTypes.number.isRequired,
    totalCount: PropTypes.number.isRequired,
  }).isRequired,
  onPageChange: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  onCouponStatusChanged: PropTypes.func.isRequired,
  onShowingCouponDetails: PropTypes.func.isRequired,
};

CouponsList.defaultProps = {
  isLoading: false,
};

export default CouponsList;
